'use client';

import React, { useEffect, useRef } from 'react';
import EpisodeCard, { EpisodeCardData } from './EpisodeCard';
import MilestoneCard, { MilestoneData } from './MilestoneCard';

type GridItem =
  | { type: 'episode'; data: EpisodeCardData }
  | { type: 'milestone'; data: MilestoneData };

interface EpisodeGridProps {
  items: GridItem[];
  sectionLabel?: string;
}

const EpisodeGrid: React.FC<EpisodeGridProps> = ({ items, sectionLabel }) => {
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }
    );

    const elements = gridRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  return (
    <div ref={gridRef} className="max-w-7xl mx-auto px-6 py-12">
      {sectionLabel && (
        <div className="flex items-center gap-4 mb-8 reveal">
          <div
            className="flex-1 h-px"
            style={{ background: 'var(--color-cream-border)' }}
          />
          <span
            className="section-label px-4"
            style={{ color: 'var(--color-charcoal-light)' }}
          >
            {sectionLabel}
          </span>
          <div
            className="flex-1 h-px"
            style={{ background: 'var(--color-cream-border)' }}
          />
        </div>
      )}

      {/* Masonry grid */}
      <div className="masonry-grid">
        {items.map((item, idx) => {
          if (item.type === 'milestone') {
            return (
              <div key={item.data.id} className="masonry-item span-2">
                <MilestoneCard milestone={item.data} />
              </div>
            );
          }
          return (
            <div key={item.data.id} className="masonry-item">
              <EpisodeCard
                episode={item.data}
                revealDelay={(idx % 5) + 1}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EpisodeGrid;